import React, { useId, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { teamPlayersQuery } from './TeamStatisticsPlayers';
import { teamDisciplineFromPlayers, teamDisciplineSummary } from '../../utils/teamDiscipline';

function DisciplineTable({ title, rows }) {
  const { t, i18n } = useTranslation('stats');
  const titleId = useId();
  return <section className="rr-panel" aria-labelledby={titleId}>
    <h3 id={titleId}>{title}</h3>
    <div className="rr-data-table ts-discipline-scroll" tabIndex={0} role="region" aria-label={title}>
      <table aria-label={title}>
        <thead><tr>{['metric', 'total', 'home', 'away'].map(key =>
          <th key={key} scope="col">{t(`teams.discipline.${key}`)}</th>)}</tr></thead>
        <tbody>{rows.map(row => <tr key={row.key}><th scope="row">{row.label || t(`teams.discipline.${row.key}`)}</th>
          {['total', 'home', 'away'].map(scope => <td key={scope}>
            {row.values[scope] === null ? '—' : row.values[scope].toLocaleString(i18n.language, { maximumFractionDigits: 2 })}
          </td>)}
        </tr>)}</tbody>
      </table>
    </div>
  </section>;
}

export default function TeamStatisticsDiscipline({ statistics }) {
  const { t, i18n } = useTranslation('stats');
  const navigate = useNavigate();
  const titleId = useId();
  const [selection, setSelection] = useState('all');
  const stored = teamDisciplineSummary(statistics, selection);
  const needsPlayers = !stored.hasData && ['idEquipo', 'idCompeticion', 'idGrupo', 'idTemporada']
    .every(key => statistics?.[key] != null && String(statistics[key]) !== '');
  const players = useQuery({ ...teamPlayersQuery(statistics || {}, navigate), enabled: needsPlayers });
  const data = needsPlayers && players.isSuccess
    ? teamDisciplineSummary(teamDisciplineFromPlayers(statistics, players.data), selection) : stored;
  return <section className="ts-discipline" aria-labelledby={titleId}>
    <header className="ts-section-heading"><span className="rr-eyebrow">{t('teams.discipline.eyebrow')}</span>
      <h2 id={titleId}>{t('teams.tabs.discipline')}</h2><p>{t('teams.discipline.subtitle')}</p></header>
    {needsPlayers && players.isLoading && <p role="status" className="rr-empty">{t('teams.modern.loadingPlayers')}</p>}
    {needsPlayers && players.isError && <Alert severity="error" action={<Button onClick={() => players.refetch()}>
      {t('teams.errors.retry')}</Button>}>{t('teams.modern.playersError')}</Alert>}
    {!data.hasData && !(needsPlayers && (players.isLoading || players.isError))
      && <p className="rr-empty">{t('teams.discipline.empty')}</p>}
    {data.hasData && <>
      {needsPlayers && <p className="ts-table-note">{t('teams.discipline.fromPlayers')}</p>}
      <section className="rr-panel" aria-label={t('teams.discipline.totals')}>
        <dl className="ts-kpis ts-discipline-kpis">{data.counts.filter(row => row.key !== 'totalCards').map(row =>
          <div key={row.key}><dt>{t(`teams.discipline.${row.key}`)}</dt>
            <dd>{row.values.total === null ? '—' : row.values.total.toLocaleString(i18n.language)}</dd></div>)}</dl>
        <p className="ts-table-note">{t('teams.discipline.weights')}</p>
      </section>
      <DisciplineTable title={t('teams.discipline.totals')} rows={data.counts} />
      <DisciplineTable title={t('teams.discipline.rates')} rows={data.rates} />
      <p className="ts-table-note">{t('teams.discipline.sample', {
        total: data.played.total ?? '—', home: data.played.home ?? '—', away: data.played.away ?? '—',
      })}</p>
      <div className="ts-discipline-controls">
        <label htmlFor={`${titleId}-card`}>{t('teams.discipline.cardType')}</label>
        <select id={`${titleId}-card`} value={selection} onChange={event => setSelection(event.target.value)}>
          {['all', 'yellow', 'secondYellow', 'red'].map(key =>
            <option key={key} value={key}>{t(`teams.discipline.${key}`)}</option>)}
        </select>
      </div>
      <DisciplineTable title={t('teams.discipline.minutes')} rows={data.intervals.map(row => ({
        ...row, label: row.key === 'unassigned' ? t('teams.discipline.unassigned')
          : t('teams.discipline.interval', {
            from: Number(row.key.split('_')[0]), to: Number(row.key.split('_')[1]) - 1,
          }),
      }))} />
      <p className="ts-table-note">{t('teams.discipline.minutesNote')}</p>
      <div className="ts-discipline-details">
        <DisciplineTable title={t('teams.discipline.states')} rows={data.states} />
        <DisciplineTable title={t('teams.discipline.roles')} rows={data.roles} />
      </div>
      {data.hasCoaches && <DisciplineTable title={t('teams.discipline.coaches')} rows={data.coaches} />}
      <p className="ts-table-note">{t('teams.discipline.method')}</p>
    </>}
  </section>;
}
